import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Modal,
  Descriptions,
  Typography,
  message,
  Tooltip,
  Empty,
  Divider,
  Form,
  InputNumber,
  Tabs,
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  ExportOutlined,
  ReloadOutlined,
  EyeOutlined,
  RollbackOutlined,
  FilterOutlined,
  ClearOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { transactionService } from '../services/transactionService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

// Each tab is a slice of the underlying transaction list, distinguished by
// status. The Invoices tab also exposes the Download Invoice action; the
// Refunds tab swaps in refund-specific columns.
const TAB_STATUS_FILTER = {
  all: undefined,
  invoices: ['captured', 'refunded', 'partially_refunded'],
  refunds: ['refunded', 'partially_refunded'],
};

const Transactions = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    status: undefined,
    planType: undefined,
    paymentMethod: undefined,
    dateRange: null,
    search: '',
  });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [summaryStats, setSummaryStats] = useState(null);

  const fetchTransactions = useCallback(
    async (page = 1, pageSize = 20, overrideStatus) => {
      setLoading(true);
      try {
        // For the Invoices and Refunds tabs we constrain status server-side
        // unless the user has chosen a more specific filter from the dropdown.
        const tabStatuses = TAB_STATUS_FILTER[activeTab];
        const effectiveStatus =
          overrideStatus !== undefined
            ? overrideStatus
            : filters.status || (tabStatuses?.length === 1 ? tabStatuses[0] : undefined);

        const params = {
          page,
          limit: pageSize,
          ...filters,
          status: effectiveStatus,
          startDate: filters.dateRange?.[0]?.toISOString(),
          endDate: filters.dateRange?.[1]?.toISOString(),
        };

        const response = await transactionService.getTransactions(params);
        if (response.success) {
          let rows = response.data.transactions;
          // For Invoices/Refunds tabs that match multiple statuses we filter
          // client-side after the fetch (the GET endpoint takes a single
          // status). Acceptable while volume is small; flip to a backend
          // status:in[...] param later.
          if (Array.isArray(tabStatuses) && !filters.status) {
            rows = rows.filter((t) => tabStatuses.includes(t.status));
          }
          setTransactions(rows);
          setPagination({
            current: response.data.pagination.page,
            pageSize: response.data.pagination.limit,
            total: response.data.pagination.total,
          });
        }
      } catch (error) {
        message.error('Failed to fetch transactions');
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [activeTab, filters]
  );

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await transactionService.getStats();
      if (response.success) setSummaryStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleTableChange = (paginationConfig) => {
    fetchTransactions(paginationConfig.current, paginationConfig.pageSize);
  };

  const handleSearch = () => fetchTransactions(1, pagination.pageSize);
  const handleReset = () => {
    setFilters({
      status: undefined,
      planType: undefined,
      paymentMethod: undefined,
      dateRange: null,
      search: '',
    });
    fetchTransactions(1, pagination.pageSize);
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailModalVisible(true);
  };

  const handleRefund = (transaction) => {
    setSelectedTransaction(transaction);
    setRefundModalVisible(true);
  };

  const processRefund = async (values) => {
    setRefundLoading(true);
    try {
      const response = await transactionService.processRefund(
        selectedTransaction._id,
        values.reason,
        values.amount
      );
      if (response.success) {
        message.success('Refund processed successfully');
        setRefundModalVisible(false);
        fetchTransactions(pagination.current, pagination.pageSize);
        fetchStats();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleDownloadInvoice = async (transaction) => {
    const hide = message.loading('Generating invoice…', 0);
    try {
      await transactionService.downloadInvoice(transaction._id, transaction.invoiceNumber);
      hide();
      message.success('Invoice downloaded');
    } catch (error) {
      hide();
      message.error(error.message || 'Failed to download invoice');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        ...filters,
        startDate: filters.dateRange?.[0]?.toISOString(),
        endDate: filters.dateRange?.[1]?.toISOString(),
      };
      await transactionService.exportTransactions(params);
      message.success('Export started. Download will begin shortly.');
    } catch (error) {
      message.error('Failed to export transactions');
    }
  };

  const getStatusTag = (status) => {
    const config = {
      created: { color: 'default', icon: <ClockCircleOutlined /> },
      authorized: { color: 'processing', icon: <ClockCircleOutlined /> },
      captured: { color: 'success', icon: <CheckCircleOutlined /> },
      failed: { color: 'error', icon: <CloseCircleOutlined /> },
      refunded: { color: 'warning', icon: <RollbackOutlined /> },
      partially_refunded: { color: 'orange', icon: <RollbackOutlined /> },
      cancelled: { color: 'default', icon: <CloseCircleOutlined /> },
      expired: { color: 'default', icon: <ClockCircleOutlined /> },
    }[status] || { color: 'default' };
    return (
      <Tag color={config.color} icon={config.icon}>
        {status?.toUpperCase().replace('_', ' ')}
      </Tag>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `₹${(amount / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const baseColumns = useMemo(
    () => [
      {
        title: 'Invoice #',
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        width: 150,
        render: (text, record) => (
          <Tooltip title={record.razorpayOrderId}>
            <Text copyable={{ text: record.razorpayOrderId }}>
              {text || record.razorpayOrderId?.slice(-10)}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: 'Organization',
        dataIndex: 'organizationName',
        key: 'organizationName',
        width: 200,
        render: (text, record) => (
          <div>
            <Text strong>{text || record.organizationId?.name || '-'}</Text>
            {record.userEmail && (
              <div>
                <Text type="secondary" className="text-xs">
                  {record.userEmail}
                </Text>
              </div>
            )}
          </div>
        ),
      },
      {
        title: 'Plan',
        dataIndex: 'planName',
        key: 'planName',
        width: 140,
        render: (text, record) => (
          <div>
            <Text>{text || '-'}</Text>
            {record.billingPeriod && (
              <div>
                <Tag>{record.billingPeriod}</Tag>
              </div>
            )}
          </div>
        ),
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 120,
        align: 'right',
        render: (amount) => <Text strong>{formatCurrency(amount)}</Text>,
      },
    ],
    []
  );

  // Columns vary per tab. We compose them so each tab shows the most
  // relevant attributes without duplicating Invoice # / Org / Plan / Amount.
  const transactionColumns = [
    ...baseColumns,
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 110,
      render: (m) => (m ? <Tag>{m.toUpperCase()}</Tag> : '-'),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(date).format('DD MMM YYYY HH:mm')}
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
          </Tooltip>
          {record.status === 'captured' && (
            <Tooltip title="Process Refund">
              <Button
                type="text"
                danger
                icon={<RollbackOutlined />}
                onClick={() => handleRefund(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const invoiceColumns = [
    ...baseColumns,
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Paid On',
      dataIndex: 'paidAt',
      key: 'paidAt',
      width: 160,
      render: (date) =>
        date ? (
          <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
            {dayjs(date).format('DD MMM YYYY HH:mm')}
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
          </Tooltip>
          <Tooltip title="Download Invoice">
            <Button
              type="primary"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadInvoice(record)}
            >
              Invoice
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const refundColumns = [
    ...baseColumns,
    {
      title: 'Refund Amount',
      dataIndex: 'refundAmount',
      key: 'refundAmount',
      width: 140,
      align: 'right',
      render: (amt) => (
        <Text strong type="warning">
          {formatCurrency(amt)}
        </Text>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'refundReason',
      key: 'refundReason',
      width: 220,
      ellipsis: true,
      render: (r) => r || '-',
    },
    {
      title: 'Refunded By',
      dataIndex: 'refundedBy',
      key: 'refundedBy',
      width: 180,
      render: (r) => r || '-',
    },
    {
      title: 'Refunded At',
      dataIndex: 'refundedAt',
      key: 'refundedAt',
      width: 160,
      render: (date) =>
        date ? (
          <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
            {dayjs(date).format('DD MMM YYYY HH:mm')}
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} />
          </Tooltip>
          <Tooltip title="Download Invoice">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadInvoice(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filtersBar = (
    <Card className="mb-4">
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Search by Order ID, Email, Invoice #…"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onPressEnter={handleSearch}
            allowClear
          />
        </Col>
        {activeTab === 'all' && (
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="created">Created</Option>
              <Option value="captured">Captured</Option>
              <Option value="failed">Failed</Option>
              <Option value="refunded">Refunded</Option>
              <Option value="partially_refunded">Partially Refunded</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
        )}
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Plan Type"
            value={filters.planType}
            onChange={(value) => setFilters({ ...filters, planType: value })}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="basic">Basic</Option>
            <Option value="premium">Premium</Option>
            <Option value="professional">Professional</Option>
            <Option value="enterprise">Enterprise</Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Select
            placeholder="Payment Method"
            value={filters.paymentMethod}
            onChange={(value) => setFilters({ ...filters, paymentMethod: value })}
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="card">Card</Option>
            <Option value="upi">UPI</Option>
            <Option value="netbanking">NetBanking</Option>
            <Option value="wallet">Wallet</Option>
          </Select>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <Space>
            <Button type="primary" icon={<FilterOutlined />} onClick={handleSearch}>
              Apply Filters
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleReset}>
              Reset
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchTransactions(pagination.current, pagination.pageSize)}
            >
              Refresh
            </Button>
            <Button icon={<ExportOutlined />} onClick={handleExport}>
              Export CSV
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  const renderTable = (columns) => (
    <Card>
      <Table
        columns={columns}
        dataSource={transactions}
        rowKey="_id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} records`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        locale={{ emptyText: <Empty description="No records found" /> }}
      />
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>
          <DollarOutlined className="mr-2" />
          Transaction Management
        </Title>
        <Text type="secondary">View and manage all payment transactions, invoices and refunds</Text>
      </div>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Total Revenue"
              value={summaryStats?.overall?.totalRevenue ? summaryStats.overall.totalRevenue / 100 : 0}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Successful Transactions"
              value={summaryStats?.overall?.successfulTransactions || 0}
              valueStyle={{ color: '#52c41a' }}
              suffix={
                <Text type="secondary" className="text-sm">
                  / {summaryStats?.overall?.totalTransactions || 0}
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Failed Transactions"
              value={summaryStats?.overall?.failedTransactions || 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Total Refunds"
              value={summaryStats?.overall?.totalRefunds ? summaryStats.overall.totalRefunds / 100 : 0}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          // Reset filters that don't apply on the new tab.
          setFilters((f) => ({ ...f, status: undefined }));
        }}
        items={[
          {
            key: 'all',
            label: (
              <span>
                <UnorderedListOutlined /> Transactions
              </span>
            ),
            children: (
              <>
                {filtersBar}
                {renderTable(transactionColumns)}
              </>
            ),
          },
          {
            key: 'invoices',
            label: (
              <span>
                <FileTextOutlined /> Invoices
              </span>
            ),
            children: (
              <>
                {filtersBar}
                {renderTable(invoiceColumns)}
              </>
            ),
          },
          {
            key: 'refunds',
            label: (
              <span>
                <RollbackOutlined /> Refunds
              </span>
            ),
            children: (
              <>
                {filtersBar}
                {renderTable(refundColumns)}
              </>
            ),
          },
        ]}
      />

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          ['captured', 'refunded', 'partially_refunded'].includes(selectedTransaction?.status) && (
            <Button
              key="invoice"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadInvoice(selectedTransaction)}
            >
              Download Invoice
            </Button>
          ),
          selectedTransaction?.status === 'captured' && (
            <Button
              key="refund"
              type="primary"
              danger
              icon={<RollbackOutlined />}
              onClick={() => {
                setDetailModalVisible(false);
                handleRefund(selectedTransaction);
              }}
            >
              Process Refund
            </Button>
          ),
        ]}
        width={720}
      >
        {selectedTransaction && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Order ID" span={2}>
                <Text copyable>{selectedTransaction.razorpayOrderId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Payment ID" span={2}>
                <Text copyable>{selectedTransaction.razorpayPaymentId || '-'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Number">
                {selectedTransaction.invoiceNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedTransaction.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Organization">
                {selectedTransaction.organizationName ||
                  selectedTransaction.organizationId?.name ||
                  '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedTransaction.userEmail || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Plan">
                {selectedTransaction.planName} ({selectedTransaction.billingPeriod})
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {selectedTransaction.paymentMethod?.toUpperCase() || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Amount Breakdown</Divider>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Base Amount">
                {formatCurrency(selectedTransaction.baseAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="Discount">
                {selectedTransaction.discountAmount > 0
                  ? `-${formatCurrency(selectedTransaction.discountAmount)}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={`Tax (${selectedTransaction.taxRate || 18}%)`}>
                {formatCurrency(selectedTransaction.taxAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong>{formatCurrency(selectedTransaction.amount)}</Text>
              </Descriptions.Item>
              {selectedTransaction.couponCode && (
                <Descriptions.Item label="Coupon Applied" span={2}>
                  <Tag color="green">{selectedTransaction.couponCode}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedTransaction.refundAmount && (
              <>
                <Divider>Refund</Divider>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Refunded Amount">
                    <Text strong type="warning">
                      {formatCurrency(selectedTransaction.refundAmount)}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Refunded By">
                    {selectedTransaction.refundedBy || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Reason" span={2}>
                    {selectedTransaction.refundReason || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Refunded At" span={2}>
                    {selectedTransaction.refundedAt
                      ? dayjs(selectedTransaction.refundedAt).format('DD MMM YYYY HH:mm:ss')
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {selectedTransaction.gstDetails?.gstin && (
              <>
                <Divider>GST Details</Divider>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="GSTIN" span={2}>
                    {selectedTransaction.gstDetails.gstin}
                  </Descriptions.Item>
                  <Descriptions.Item label="Company">
                    {selectedTransaction.gstDetails.companyName}
                  </Descriptions.Item>
                  <Descriptions.Item label="State">
                    {selectedTransaction.gstDetails.state} ({selectedTransaction.gstDetails.stateCode})
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            {selectedTransaction.errorCode && (
              <>
                <Divider>Error Details</Divider>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Error Code">
                    <Text type="danger">{selectedTransaction.errorCode}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">
                    {selectedTransaction.errorDescription}
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}

            <Divider>Timestamps</Divider>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Created At">
                {dayjs(selectedTransaction.createdAt).format('DD MMM YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Paid At">
                {selectedTransaction.paidAt
                  ? dayjs(selectedTransaction.paidAt).format('DD MMM YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Refund Modal */}
      <Modal
        title="Process Refund"
        open={refundModalVisible}
        onCancel={() => setRefundModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={processRefund}
          initialValues={{ amount: selectedTransaction?.amount }}
        >
          <div className="mb-4 p-4 bg-gray-50 rounded">
            <Text strong>Transaction: </Text>
            <Text>{selectedTransaction?.razorpayOrderId}</Text>
            <br />
            <Text strong>Amount: </Text>
            <Text>{formatCurrency(selectedTransaction?.amount)}</Text>
          </div>

          <Form.Item
            name="amount"
            label="Refund Amount (in paise)"
            rules={[
              { required: true, message: 'Please enter refund amount' },
              {
                validator: (_, value) => {
                  if (value > selectedTransaction?.amount) {
                    return Promise.reject('Refund amount cannot exceed transaction amount');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={selectedTransaction?.amount}
              formatter={(value) => `₹ ${(value / 100).toFixed(2)}`}
              parser={(value) => Math.round(parseFloat(value.replace(/₹\s?/g, '')) * 100)}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Refund Reason"
            rules={[{ required: true, message: 'Please provide a reason for the refund' }]}
          >
            <TextArea rows={3} placeholder="Enter the reason for processing this refund…" />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space className="w-full justify-end">
              <Button onClick={() => setRefundModalVisible(false)}>Cancel</Button>
              <Button type="primary" danger htmlType="submit" loading={refundLoading}>
                Process Refund
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Transactions;
